// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ChartXYPlaneSettings.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
//</copyright>
// ----------------------------------------------------------------------

import { action } from 'mobx';
import React, { Component } from 'react';
import {
  get,
  invoke,
  map,
  find,
  forEach,
  flow,
  reduce,
  getOr,
  set,
  values,
  isEqual,
  isEmpty,
  assign,
  isNil,
  isString,
  toString,
  endsWith,
  findIndex,
  concat,
  isBoolean
} from 'lodash/fp';

import ChartSettingsContainer from './Izenda.ChartSettingsContainer';
import ReportPartChartConstants from 'logic/data/Izenda.ReportPartChartConstants';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import { DATA_TYPES } from 'logic/data/Izenda.ReportDesignerConstants';
import { parser as chartDataParser } from 'share/utils/Izenda.ChartDataParser';
import localizer from 'share/localization/Izenda.Localizer';
import ChartXYPlaneSettingsAxis from './Izenda.ChartXYPlaneSettingsAxis';
import { exportClass } from 'share/Izenda.ServiceProvider';

const CONSTANTS = ReportPartChartConstants.CHART_SETTINGS.DEFAULT_VALUE;
const FONT_STYLE = ReportPartChartConstants.CHART_SETTINGS.FONT_STYLE;
const XY_PLANE_Y_AXIS_POSTION = ReportPartChartConstants.CHART_SETTINGS.XY_PLANE.XY_PLANE_Y_AXIS_POSTION;
const VALUE_LABEL_METRICS_CHART = [CHART_TYPE.HeatMap, CHART_TYPE.Bubble, CHART_TYPE.Scatter];
const OTHER_PROPS_AXIS_KEY = 'axis';
const OTHER_PROPS_THRESHOLD_KEY = 'threshold';
const WIDGET = {
  Axis: ChartXYPlaneSettingsAxis
};
const { isChartType } = ReportPartChartConstants;

/**
 * Get definition of schema
 * @param {any} xOption
 * @param {any} yOption
 * @return {any}
 */
function getSchemaDef(xOption: any, yOption: any) {
  return {
    schemaKey: 'XY_PLANE_SCHEMA',
    fields: {
      xAxisTitle: {
        title: localizer.getText('REPORTDESIGNER_TOOLTIP_X_AXIS_UPPER'),
        fieldClassName: 'axis-title-field',
        titleClassName: 'axis-title',
        controlClassName: 'axis-title-control',
        type: 'select',
        fnValue: 'getXAxisSelectedOptionValue',
        props: {
          options: xOption
        }
      },
      xAxis: {
        title: localizer.getText('REPORTDESIGNER_TOOLTIP_X_AXIS_LOWER'),
        fieldClassName: 'u_inlineBlock u_fullWidth izenda-XYPlane-XAxis u_paddingBottom',
        titleClassName: 'axis-title-properties',
        component: 'Axis',
        fnValue: 'getXAxis',
        props: {
          type: 'xAxis',
          selectedAxis: 'getXAxisSelectedOption',
          syncXAxisType: 'syncXAxisType',
          setXAxisType: 'setXAxisType',
          disabledSyncButton: 'disabledSyncButton'
        }
      },
      yAxisTitle: {
        title: localizer.getText('REPORTDESIGNER_TOOLTIP_Y_AXIS_UPPER'),
        fieldClassName: 'axis-title-field',
        titleClassName: 'axis-title',
        controlClassName: 'axis-title-control',
        type: 'select',
        fnValue: 'getYAxisSelectedOptionValue',
        props: {
          options: yOption
        }
      },
      yAxis: {
        title: localizer.getText('REPORTDESIGNER_TOOLTIP_Y_AXIS_LOWER'),
        fieldClassName: 'u_inlineBlock u_fullWidth',
        titleClassName: 'axis-title-properties',
        component: 'Axis',
        fnValue: 'getYAxis',
        props: {
          type: 'yAxis',
          selectedAxis: 'getYAxisSelectedOption'
        }
      }
    }
  };
}

@exportClass('ChartXYPlaneSettings')
export default class ChartXYPlaneSettings extends Component {
  constructor(props) {
    super(props);

    this.cloneElements();
    this.createBackupData();
    this.initData();

    const value = props.value || {};
    const xOption = value.xAxisTitleOption || [{}];
    const yOption = value.yAxisTitleOption || [{}];

    this.state = {
      selectedXAxisOption: xOption[this.expandedLevel],
      selectedYAxisOption: yOption[0]
    };
  }

  /**
   * Get value when popup is closed
   */
  getValue() {
    this.updateElements('labelsElements');
    this.updateElements('valuesElements');

    // Update preview when set xAxis-type to linear
    const {
      reportPartDetails: reportPart,
      reportPartDetails: {
        reportPartContent,
        reportPartContent: { currentDrilldownField, resetSortAscending }
      },
      fieldStore
    } = this.props;

    reportPartContent.isLinear() &&
      resetSortAscending(currentDrilldownField) &&
      fieldStore.needUpdatePreview(reportPart);

    return { metricAllOption: this.metricAllOption };
  }

  /**
   * Clone elements
   */
  cloneElements() {
    const value = this.props.value || {};
    const content = this.props.reportPartDetails.reportPartContent;
    value.labelsElements = content.labelsElements.map(field => field.clone());
    value.valuesElements = content.valuesElements.map(field => field.clone());
  }

  /**
   * Update elements
   * @param {any} type
   */
  updateElements(type: string) {
    const {
      value,
      reportPartDetails: { reportPartContent: content }
    } = this.props;

    content[type].forEach((item, index) => {
      item.setOtherProps(null, invoke('properties.getOtherProps', value[type][index]));
    });
  }

  /**
   * Create backup data
   */
  createBackupData() {
    const value = this.props.value || {};
    this.backUpData = {
      xAxis: map(
        element => ({
          backUpData: element.properties.getOtherProps(),
          element: element
        }),
        value.labelsElements
      ),
      yAxis: value.valuesElements.map(element => element.properties.getOtherProps(OTHER_PROPS_AXIS_KEY))
    };
  }

  /**
   * Init data
   * Store drilldown data to this instance
   */
  initData() {
    const props = this.props;
    const value = props.value || {};
    const reportPartDetails = props.reportPartDetails;
    this.activeSeriesKey = null;
    this.metricAllOption = value.metricAllOption;

    this.expandedLevel = reportPartDetails.reportPartContent.expandedLevel;
    this.previewData = reportPartDetails.previewData;
    this.previewDataRecords = reportPartDetails.previewDataRecords || [];
    this.previewDataFieldsMapping = reportPartDetails.previewDataFieldsMapping || [];
  }

  /**
   * Get configuration for properties editor
   * @returns {any}
   */
  getConfigurationDef() {
    const value = this.props.value || {};
    const metricAllOption = this.metricAllOption;
    const { selectedXAxisOption, selectedYAxisOption } = this.state;
    const xOption = value.xAxisTitleOption || [];
    const yOption = value.yAxisTitleOption || [];
    const backUpData = this.backUpData;

    const xField = find({ fieldName: selectedXAxisOption.value }, value.labelsElements);
    const yField = find({ fieldName: selectedYAxisOption.value }, value.valuesElements);
    this.xField = xField;
    this.yField = yField;

    return {
      definedSchema: getSchemaDef(xOption, yOption),
      widgets: WIDGET,
      source: {},
      mappingProps: {
        syncXAxisType: action(newValue => {
          forEach(field => {
            field !== this.xField && field.previewDataType !== DATA_TYPES.TEXT && setXAxisType(newValue, field);
          }, value.labelsElements);
        }),
        setXAxisType: action(newValue => {
          this.xField && setXAxisType(newValue, this.xField);
        }),
        disabledSyncButton: () => {
          return flow(
            reduce((acc, field) => {
              if (field.previewDataType === DATA_TYPES.TEXT) {
                return acc;
              }
              const isCategory = getOr(false, 'izCategory', field.properties.getOtherProps(OTHER_PROPS_AXIS_KEY));

              return set(isCategory, true, acc);
            }, {}),
            values,
            get('length'),
            isEqual(1)
          )(value.labelsElements);
        }
      },
      mappingSource: {
        getXAxis: () =>
          xField &&
          constructAxisProps(
            xField.getValueByKey(OTHER_PROPS_AXIS_KEY),
            'xAxis',
            xField.fieldName,
            xField.previewDataType
          ),
        getYAxis: () =>
          isEmpty(selectedYAxisOption.value)
            ? constructAxisProps(metricAllOption, 'yAxis', localizer.getText('COMMON_LABEL_ALL'))
            : yField &&
              constructAxisProps(
                yField.getValueByKey(OTHER_PROPS_AXIS_KEY),
                'yAxis',
                yField.fieldName,
                yField.previewDataType
              ),
        getXAxisSelectedOption: () => selectedXAxisOption,
        getYAxisSelectedOption: () => selectedYAxisOption,
        getXAxisSelectedOptionValue: () => get('value', selectedXAxisOption),
        getYAxisSelectedOptionValue: () => get('value', selectedYAxisOption)
      },
      onChangeValue: this.onChangeValue
    };

    function setXAxisType(isCategory, field) {
      if (field) {
        const overrideValue = {
          izCategory: isCategory
        };
        field.setOtherProps(OTHER_PROPS_AXIS_KEY, assign(field.getValueByKey(OTHER_PROPS_AXIS_KEY), overrideValue));
        if (isCategory) {
          // remove threshold setting if change to category
          field.setOtherProps(OTHER_PROPS_THRESHOLD_KEY, []);
        } else {
          // use back up date to revert threshold setting when change back from category to linear
          const backUp = find(item => item.element === field, backUpData.xAxis);

          field.setOtherProps(OTHER_PROPS_THRESHOLD_KEY, get(OTHER_PROPS_THRESHOLD_KEY, backUp.backUpData));
        }
      }
    }

    function constructAxisProps(axis, type, defaultText, dataType) {
      axis = axis || {};
      const style = axis['izTitle.style'] || {};
      return {
        //type: type,
        dataType: dataType,
        text: isNil(axis['izTitle.text']) ? defaultText : axis['izTitle.text'],
        enabled: isNil(axis['izTitle.enabled']) ? true : axis['izTitle.enabled'],
        font: {
          style: {
            fontBold: style.fontWeight === FONT_STYLE.BOLD,
            fontItalic: style.fontStyle === FONT_STYLE.ITALIC,
            fontUnderline: style.textDecoration === FONT_STYLE.UNDERLINE
          },
          format: {
            fontFamily: style.fontFamily,
            fontSize: style.fontSize
          },
          color: style.color || CONSTANTS.BLACK_COLOR,
          backgroundColor: style.backgroundColor
        },
        alignment: axis['izTitle.align'] || 'izMiddle',
        intervals: axis['izTickInterval'],
        categoryIntervals: axis['izTickCategoryInterval'],
        startingPoint: axis['izStartingPoint'],
        endingPoint: axis['izEndingPoint'],
        isCategory: axis['izCategory'],
        position: axis['izOpposite'] === true
          ? XY_PLANE_Y_AXIS_POSTION.RIGHT.value
          : axis['izOpposite'] === false
          ? XY_PLANE_Y_AXIS_POSTION.LEFT.value
          : XY_PLANE_Y_AXIS_POSTION.AUTO.value
      };
    }
  }

  /**
   * Get chart data
   */
  getChartData() {
    const reportPartDetails = this.props.reportPartDetails;
    const reportPartContent = reportPartDetails.reportPartContent;
    const chartType = reportPartContent.chartType;
    let key = isChartType(chartType, CHART_TYPE.Combination, CHART_TYPE.Sparkline) ? null : this.activeSeriesKey;
    key = isString(key) ? key : key && key.name;
    return chartDataParser(reportPartContent.getActiveElmsByActiveSeries(key), this.expandedLevel, {
      fieldsMapping: this.previewDataFieldsMapping,
      records: this.previewDataRecords,
      rawData: this.previewData
    });
  }

  render() {
    const { allChartProps, value } = this.props;
    const allProps = allChartProps();
    allProps.seriesOptions = [...value.labelsElements, ...value.valuesElements, ...value.separatorsElements];
    const chartOptions = {
      commonXYAxis: {
        metricAllOption: this.metricAllOption
      }
    };

    return (
      <ChartSettingsContainer
        className="xy-plane-settings"
        type={this.props.reportPartType}
        configurationDef={this.getConfigurationDef()}
        chartOptions={chartOptions}
        allChartProps={allProps}
        chartData={this.getChartData()}
      />
    );
  }

  onChangeValue = (
    schemaData: any,
    changedKey: string,
    changedKeyPath: any,
    changedValue: any,
    changedOthersInfo: any
  ) => {
    const groupData = changedKeyPath && changedKeyPath.length ? get(changedKeyPath, schemaData) : schemaData;
    const fieldData = groupData[changedKey];

    fieldData && (fieldData.value = changedValue);

    const value = this.props.value;
    const xAxisTitle = schemaData.xAxisTitle;
    const yAxisTitle = schemaData.yAxisTitle;
    const xOption = value.xAxisTitleOption || [];
    const yOption = value.yAxisTitleOption || [];

    if (changedKey === 'xAxisTitle') {
      const index = findIndex({ fieldName: xAxisTitle.value }, value.labelsElements) || 0;
      return this.props.fieldStore
        .getRandomPreviewData(index)
        .bind(this)
        .then(result => {
          result = result || {};
          this.expandedLevel = index;
          this.previewData = result;
          this.previewDataRecords = result.records || [];
          this.previewDataFieldsMapping = result.fieldsMapping || [];
        })
        .finally(() => {
          updateOption.call(this);
        });
    } else {
      this.xField &&
        this.xField.setOtherProps(
          OTHER_PROPS_AXIS_KEY,
          assign(this.xField.getValueByKey(OTHER_PROPS_AXIS_KEY), constructAxisChartOption(schemaData.xAxis || {}))
        );
      if (!get('selectedYAxisOption.value', this.state)) {
        this.metricAllOption = assign(this.metricAllOption, constructAxisChartOption(schemaData.yAxis || {}));
      } else {
        this.yField &&
          this.yField.setOtherProps(
            OTHER_PROPS_AXIS_KEY,
            assign(this.yField.getValueByKey(OTHER_PROPS_AXIS_KEY), constructAxisChartOption(schemaData.yAxis || {}))
          );
      }
      // set active series key, if the charType's field container is values container
      const chartType = get('reportPartContent.chartType', this.props.reportPartDetails);
      !isChartType(chartType, ...VALUE_LABEL_METRICS_CHART) && (this.activeSeriesKey = yAxisTitle.value || null);
      updateOption.call(this);
    }

    function constructChartStyle(font) {
      font = font || {};
      font.style = font.style || {};
      font.format = font.format || {};

      const fontSize = font.format.fontSize;
      return {
        fontWeight: font.style.fontBold ? FONT_STYLE.BOLD : '',
        fontStyle: font.style.fontItalic ? FONT_STYLE.ITALIC : '',
        textDecoration: font.style.fontUnderline ? FONT_STYLE.UNDERLINE : '',
        fontSize:
          isNil(fontSize) ||
          flow(
            toString,
            endsWith('px')
          )(fontSize)
            ? fontSize
            : fontSize + 'px',
        fontFamily: font.format.fontFamily,
        color: font.color,
        backgroundColor: font.backgroundColor
      };
    }

    function constructAxisChartOption(axis) {
      const font = axis.value.font || {};
      const isCategory = axis.value.isCategory;

      return {
        'izTitle.enabled': axis.value.enabled,
        'izTitle.text': axis.value.text,
        'izTitle.style': constructChartStyle(font),
        'izTitle.align': axis.value.alignment,
        izTickInterval: axis.value.intervals,
        izTickCategoryInterval: axis.value.categoryIntervals,
        izStartingPoint: axis.value.startingPoint,
        izEndingPoint: axis.value.endingPoint,
        izStartOnTick: isNil(axis.value.startingPoint),
        izCategory: isCategory,
        izOpposite: axis.value.position === XY_PLANE_Y_AXIS_POSTION.RIGHT.value
          ? true
          : axis.value.position === XY_PLANE_Y_AXIS_POSTION.LEFT.value
          ? false
          : null
      };
    }

    function updateOption() {
      const xAxisDateTimeFormat = find({ value: xAxisTitle.value }, xOption);
      const yAxisDateTimeFormat = find({ value: yAxisTitle.value }, yOption);

      this.setState({
        selectedXAxisOption: {
          value: xAxisTitle.value,
          dateTimeFormatData: xAxisDateTimeFormat.dateTimeFormatData
        },
        selectedYAxisOption: {
          value: yAxisTitle.value,
          dateTimeFormatData: yAxisDateTimeFormat.dateTimeFormatData
        }
      });
    }
  };
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ChartXYPlaneSettings.js